import { extends as _extends, objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useMemo, useState, useEffect, createElement, Fragment, cloneElement } from 'react';
import cn from 'classnames';
import { v4 } from 'uuid';
import keycode from 'keycode';
import { useGaudiLocalization } from '../Provider/Provider.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { usePopoverState } from 'reakit/Popover';
import { useFlattenedData, findByValue, setInputValueByRef } from '../Listbox/util.js';

var _excluded = ["aria-label", "aria-labelledby", "data", "disabled", "emptyValue", "id", "multiselect", "onClick", "readOnly", "showSelectedIcon", "value"];
var useSelect = function useSelect(_ref, ref) {
  var ariaLabel = _ref['aria-label'],
      ariaLabelledBy = _ref['aria-labelledby'],
      _ref$data = _ref.data,
      data = _ref$data === void 0 ? [] : _ref$data,
      disabled = _ref.disabled,
      emptyValue = _ref.emptyValue,
      nativeId = _ref.id,
      multiselect = _ref.multiselect,
      onClick = _ref.onClick,
      readOnly = _ref.readOnly,
      _ref$showSelectedIcon = _ref.showSelectedIcon,
      showSelectedIcon = _ref$showSelectedIcon === void 0 ? false : _ref$showSelectedIcon,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? emptyValue : _ref$value,
      otherProps = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _useGaudiLocalization = useGaudiLocalization(),
      texts = _useGaudiLocalization.texts;

  var searchData = useFlattenedData(data);
  var inputRef = useProxiedRef(ref);
  var popover = usePopoverState({
    gutter: 4,
    placement: 'bottom-start'
  });
  var id = useMemo(function () {
    return nativeId || "select_" + v4();
  }, [nativeId]);
  var buttonId = id + "-button"; // support 'escape' resetting to the value that was set when the listbox opened

  var _React$useState = useState(value),
      lastValue = _React$useState[0],
      setLastValue = _React$useState[1];

  useEffect(function () {
    setLastValue(value);
  }, [popover.visible]); // event handlers

  var handleButtonClick = function handleButtonClick(event) {
    popover.toggle();

    if (onClick) {
      event.persist();
      onClick(event);
    }
  };

  var handleButtonKeyDown = function handleButtonKeyDown(event) {
    if (event.keyCode === keycode('up') || event.keyCode === keycode('down')) {
      event.preventDefault();
      popover.toggle();
    }
  };

  var handleListboxKeyDown = function handleListboxKeyDown(event) {
    switch (event.keyCode) {
      case keycode('escape'):
        {
          event.preventDefault();

          if (lastValue !== undefined) {
            setInputValueByRef(inputRef.current, lastValue);
          }

          popover.hide();
          break;
        }

      case keycode('tab'):
      case keycode('enter'):
        {
          if (event.keyCode !== keycode('tab')) {
            event.preventDefault();
          }

          popover.hide();
          break;
        }
    }
  };

  var handleListboxClick = function handleListboxClick(event) {
    event.preventDefault();

    if (!multiselect) {
      popover.hide();
    }
  };

  var text = '';
  var more = 0;

  if (value !== undefined) {
    if (multiselect) {
      var selectedValues = (value === null || value === void 0 ? void 0 : value.toString().split(',')) || [];

      if (selectedValues.length === searchData.filter(function (item) {
        return !item.disabled;
      }).length) {
        text = texts.select.allOptionsSelected;
      } else {
        var _findByValue$text, _findByValue;

        text = (_findByValue$text = (_findByValue = findByValue(searchData, selectedValues[0])) === null || _findByValue === void 0 ? void 0 : _findByValue.text) !== null && _findByValue$text !== void 0 ? _findByValue$text : '';
        more = selectedValues.length > 1 ? selectedValues.length - 1 : 0;
      }
    } else {
      var item = findByValue(searchData, value);

      if (item) {
        text = showSelectedIcon && item.icon ? createElement(Fragment, null, cloneElement(item.icon, {
          className: cn(item.icon.props.className, 'mr-1')
        }), item.text) : item.text;
      }
    }
  }

  var button = {
    'aria-expanded': popover.visible,
    'aria-haspopup': 'listbox',
    'aria-label': ariaLabel ? ariaLabel + " " + text : undefined,
    'aria-labelledby': ariaLabelledBy ? ariaLabelledBy + " " + buttonId : undefined,
    disabled: disabled,
    id: buttonId,
    onClick: !disabled && !readOnly ? handleButtonClick : undefined,
    onKeyDown: !disabled && !readOnly ? handleButtonKeyDown : undefined,
    type: 'button'
  };

  var listbox = _extends({}, otherProps, {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    data: data,
    disabled: disabled,
    emptyValue: emptyValue,
    onClick: handleListboxClick,
    onKeyDown: handleListboxKeyDown,
    ref: inputRef,
    value: value,
    multiselect: multiselect
  });

  return {
    button: button,
    listbox: listbox,
    popover: _extends({}, popover, {
      container: {
        tabIndex: -1,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy
      }
    }),
    text: text,
    more: more
  };
};

export { useSelect };
//# sourceMappingURL=useSelect.js.map
